/* eslint-disable @typescript-eslint/no-unused-vars */
import { Calendar, Views } from "react-big-calendar";
import dayjs, { setDateTime } from "global/dayjs";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  createNewEvent,
  editEvent,
  getAllEventsForUser,
  getOverrideWorkingHours,
  getStandardWorkingHours,
  removeEvent,
  selectEvents,
  selectIsLoading,
  selectOverrideWorkingHours,
  selectStandardWorkingHours,
} from "./CPLCalendarSlice";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import "./CPLCalendar.scss";
import { CPLCalendarEvent, NewCPLCalendarEvent } from "services/classes/CPLCalendarEvent";
import FormModal from "cpl-react-common/components/Modals/FormModal";
import DateTimeInput from "cpl-react-common/components/Inputs/DateTimeInput";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClockRotateLeft, faEdit, faPlus, faSave, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import useAutosaveValue from "app/modules/shared/autosave/useAutosaveValue";
import Autosave from "app/modules/shared/autosave/Autosave";
import WorkWeekView from "./WorkWeekView";
import { useLocalizer } from "setup/customHooks/Localizer";
import { CalendarEventTypes } from "global/enums";
import CalendarEventTypesKey from "./CalendarEventTypesKey";
import { Permission } from "services/userSession/UserPermittedComponent";
import { Permissions } from "cpl-react-common/components/Enums/Permissions";

const AUTOSAVE_FORM_KEY = "NewCalendarEvent";

const validationSchema = Yup.object().shape({
  start: Yup.date().max(Yup.ref("end"), "Start must be before end").required("Start is required"),
  end: Yup.date().min(Yup.ref("start"), "End must be after start").required("End is required"),
  title: Yup.string().required("Title is required"),
  description: Yup.string().nullable(),
  createdBy: Yup.string(),
  createdDate: Yup.date(),
  updatedBy: Yup.string().nullable(),
  updatedDate: Yup.date().nullable(),
  completed: Yup.boolean(),
  deleted: Yup.boolean(),
  allDay: Yup.boolean(),
});

export const eventRenderProps = (event: CPLCalendarEvent) => {
  let classes = "";
  if (event.completed) classes = classes + "completed ";

  switch (event.calendarEventTypeID) {
    case CalendarEventTypes.UserTask:
      classes += "user-task ";
      break;
    case CalendarEventTypes.Meeting:
      classes += "meeting ";
      break;
    case CalendarEventTypes.Event:
      classes += "event ";
      break;
    default:
      break;
  }
  return { className: classes, style: {} };
};

export const CPLCalendar = () => {
  const localizer = useLocalizer();
  const thunkDispatch = useThunkDispatch();
  const events = useSelector(selectEvents);
  const isLoading = useSelector(selectIsLoading);
  const standardWorkingHours = useSelector(selectStandardWorkingHours);
  const overrideWorkingHours = useSelector(selectOverrideWorkingHours);

  const navigate = useNavigate();
  const startDate = new Date();
  startDate.setMinutes(Math.ceil(startDate.getMinutes() / 30) * 30 + 30);
  const endDate = dayjs(startDate).add(30, "m").toDate();
  const [selectedEvent, setSelectedEvent] = useState<CPLCalendarEvent>(
    new CPLCalendarEvent({ start: startDate, end: endDate })
  );
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEvent, setNewEvent] = useState(false);
  const [autosavedValues, clearAutosavedValues, reloadAutosavedValues] = useAutosaveValue<CPLCalendarEvent>(
    AUTOSAVE_FORM_KEY,
    new CPLCalendarEvent({ start: startDate, end: endDate }),
    (savedValue) =>
      new CPLCalendarEvent({
        ...savedValue,
      })
  );

  const onSelectEvent = useCallback((event: CPLCalendarEvent) => {
    setNewEvent(false);
    setSelectedEvent(event);
    setShowEventModal(true);
  }, []);

  const onNewEventButtonClick = useCallback(() => {
    setSelectedEvent(autosavedValues);
    setNewEvent(true);
    setShowEventModal(true);

    const nowDate = new Date();

    if (!autosavedValues?.start) autosavedValues.start = nowDate;

    if (!autosavedValues?.end) {
      autosavedValues.end = nowDate;
    }
  }, [autosavedValues]);

  const onSelectTime = useCallback((selectedStart: Date, selectedEnd: Date) => {
    const calendarEvent = new CPLCalendarEvent();
    calendarEvent.start = selectedStart;
    calendarEvent.end = selectedEnd;
    setSelectedEvent(calendarEvent);
    setNewEvent(true);
    setShowEventModal(true);
  }, []);

  const onAddNewEvent = useCallback((event: NewCPLCalendarEvent) => {
    setShowEventModal(false);
    thunkDispatch(createNewEvent({ event }));
    clearAutosavedValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEditEvent = useCallback((event: CPLCalendarEvent) => {
    thunkDispatch(editEvent({ event }));
    setShowEventModal(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveEvent = useCallback((eventID: number) => {
    thunkDispatch(removeEvent({ eventID }));
    setShowEventModal(false);
    clearAutosavedValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    thunkDispatch(getAllEventsForUser());
    thunkDispatch(getStandardWorkingHours());
    thunkDispatch(getOverrideWorkingHours());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slotRenderProps = (date: Date) => {
    if (standardWorkingHours.length === 0 && overrideWorkingHours.length === 0) return {};

    const overrideHours = overrideWorkingHours.find((i) => {
      const startDateMatch = dayjs(i.overrideStartDate).isSameOrBefore(date, "day");
      const endDateMatch = dayjs(i.overrideEndDate).isSameOrAfter(date, "day");
      return startDateMatch && endDateMatch;
    });

    if (overrideHours) {
      const openTimeDate = setDateTime(dayjs(date).startOf("day"), overrideHours.openTime);
      const closeTimeDate = setDateTime(dayjs(date).startOf("day"), overrideHours.closeTime);
      if (overrideHours.isOpen && dayjs(date).isSameOrAfter(openTimeDate) && dayjs(date).isBefore(closeTimeDate)) {
        return {};
      } else {
        return { className: "rbc-off-range-bg" };
      }
    }

    const day = standardWorkingHours.find((i) => i.dayOfWeek! - 1 === dayjs(date).weekday());
    if (!day) {
      return { className: "rbc-off-range-bg" };
    }
    const dayStart = dayjs(date)
      .startOf("week")
      .add(day.dayOfWeek! - 1, "day");
    const openTime = setDateTime(dayStart, day.openTime);
    const closeTime = setDateTime(dayStart, day.closeTime);
    if (!day.isOpen || dayjs(date).isBefore(openTime) || dayjs(date).isSameOrAfter(closeTime))
      return { className: "rbc-off-range-bg" };
    else return {};
  };

  const modalHeaderButtons = (
    <div>
      <Link
        data-test-id="calendar-event-modal__view-event-history-link"
        className="btn btn-icon btn-warning"
        to={`/Calendar/History/${selectedEvent.calendarEventID!}`}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title="View event history"
      >
        <FontAwesomeIcon icon={faClockRotateLeft} />
      </Link>
      <Permission permissionPredicate={(permission) => permission === Permissions.EditCalendarEvents}>
        <button
          data-test-id="calendar-event-modal__remove-event-from-calendar-button"
          type="button"
          onClick={() => onRemoveEvent(selectedEvent.calendarEventID!)}
          className="btn btn-danger btn-icon"
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title="Remove event from calendar"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </Permission>
    </div>
  );

  const modalFooterButtons = (
    <>
      {newEvent ? (
        <Permission permissionPredicate={(permission) => permission === Permissions.CreateCalendarEvents}>
          <button
            data-test-id="calendar-event-modal__create-submit-button"
            form="event-form"
            type="submit"
            className="btn btn-success btn-icon"
          >
            <FontAwesomeIcon icon={faSave} />{" "}
          </button>
        </Permission>
      ) : (
        <Permission permissionPredicate={(permission) => permission === Permissions.EditCalendarEvents}>
          <button
            data-test-id="calendar-event-modal__edit-submit-button"
            form="event-form"
            type="submit"
            className="btn btn-success btn-icon"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </Permission>
      )}
    </>
  );

  const actionButtons = (existingEvent: CPLCalendarEvent) => {
    let innerAction: JSX.Element | null = null;
    if (existingEvent.userTaskID !== undefined && existingEvent.userTaskID !== null) {
      innerAction = (
        <button
          data-test-id="calendar-event-modal__view-user-task-button"
          className="btn btn-secondary case-button mb-6"
          type="button"
          onClick={() => {
            clearAutosavedValues();
            navigate(`/UserTask/${selectedEvent.userTaskID}`);
          }}
        >
          View User Task
        </button>
      );
    } else if (existingEvent.meetingID != undefined && existingEvent.meetingID !== null) {
      innerAction = (
        <button
          data-test-id="calendar-event-modal__view-meeting-button"
          className="btn btn-secondary case-button mb-6"
          type="button"
          onClick={() => {
            clearAutosavedValues();
            navigate(`/Meeting/${selectedEvent.meetingID}`);
          }}
        >
          View Meeting
        </button>
      );
    }

    return innerAction != null ? (
      <div className="row">
        <div className="col-sm-12">
          <label className="col-form-label fw-bold fs-6">Actions:</label>
          <div>{innerAction}</div>
        </div>
      </div>
    ) : (
      <></>
    );
  };

  return (
    <ActivityIndicator isBusy={isLoading}>
      <FormModal
        modalTitle={newEvent ? "New Event" : selectedEvent.title!}
        showModal={showEventModal}
        dismissModalCallback={() => {
          setShowEventModal(false);
          if (newEvent) {
            reloadAutosavedValues();
          }
        }}
        footerActionButtons={modalFooterButtons}
        headerActionButtons={!newEvent && modalHeaderButtons}
      >
        <Formik
          initialValues={selectedEvent}
          onSubmit={newEvent ? onAddNewEvent : onEditEvent}
          validationSchema={validationSchema}
        >
          {({ values, setFieldValue, setFieldTouched }) => (
            <Form id="event-form">
              <Autosave formKey={AUTOSAVE_FORM_KEY} />
              <div className="row">
                <div className="col-sm-6">
                  <label className="col-form-label required fw-bold fs-6">Event Title:</label>
                  <Field
                    data-test-id="calendar-event-modal__title-input"
                    name="title"
                    type="text"
                    className="form-control"
                    placeholder={values?.title ?? "Enter event title..."}
                    value={values?.title ?? ""}
                  ></Field>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <ErrorMessage name="title" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-6">
                  <label className="col-form-label required fw-bold fs-6">Start:</label>
                  <div className="form-control form-control-solid">
                    <DateTimeInput
                      data-test-id="calendar-event-modal__start-date-input"
                      date={values?.start ?? undefined}
                      handleChange={(e) => {
                        setFieldTouched("start", true);
                        setFieldValue("start", e);
                      }}
                    />
                  </div>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <ErrorMessage name="start" />
                    </div>
                  </div>
                </div>
                <div className="col-sm-6">
                  <label className="col-form-label required fw-bold fs-6">End:</label>
                  <div className="form-control form-control-solid">
                    <DateTimeInput
                      data-test-id="calendar-event-modal__end-date-input"
                      date={values?.end ?? undefined}
                      handleChange={(e) => {
                        setFieldTouched("end", true);
                        setFieldValue("end", e);
                      }}
                    />
                  </div>
                  <div className="fv-plugins-message-container">
                    <div className="fv-help-block">
                      <ErrorMessage name="end" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <label className="col-form-label fw-bold fs-6">Description:</label>
                  <Field
                    data-test-id="calendar-event-modal__description-input"
                    name={"description"}
                    input="textarea"
                    className="form-control"
                    placeholder={values?.description ?? "Enter event description..."}
                    value={values?.description ?? ""}
                  ></Field>
                </div>
              </div>
              {!newEvent && <>{actionButtons(selectedEvent)}</>}
            </Form>
          )}
        </Formik>
      </FormModal>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Calendar</div>
          <div className="card-toolbar">
            <Permission permissionPredicate={(permission) => permission === Permissions.CreateCalendarEvents}>
              <button
                data-test-id="calendar-page__create-new-event-button"
                className="btn btn-icon btn-primary"
                onClick={onNewEventButtonClick}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="Create new event"
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </Permission>
          </div>
        </div>
        <div className="card-body">
          <Calendar
            dayLayoutAlgorithm={"no-overlap"}
            defaultView={Views.WORK_WEEK}
            events={events}
            localizer={localizer}
            onSelectEvent={onSelectEvent}
            onSelectSlot={(slotInfo) => onSelectTime(slotInfo.start, slotInfo.end)}
            eventPropGetter={eventRenderProps}
            slotPropGetter={slotRenderProps}
            showMultiDayTimes={true}
            views={{
              month: true,
              week: true,
              work_week: WorkWeekView,
              agenda: true,
            }}
            selectable
          />
          <div className="mt-4 fs-5">
            <CalendarEventTypesKey />
          </div>
        </div>
      </div>
    </ActivityIndicator>
  );
};
